import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Service } from '@app/services/_models/service.model';
import { Status } from '@models/status.model';
import { ServiceType } from '@models/service-type.model';
import { NomenclatureService } from '@services/nomenclature.service';
import { EMPTY, forkJoin, Observable, of, OperatorFunction, Subscription } from 'rxjs';
import { catchError, concatMap, debounceTime, first, repeatWhen, switchMap, tap } from 'rxjs/operators';
import { CashbackService } from '@app/cashbacks/_services/cashback.service';
import { ListCashback } from '@app/cashbacks/_models/list-cashbacks.model';
import { TranslateService } from '@ngx-translate/core';
import { PermissionsService } from '@app/login/_services/permissions.service';
import { BaseSortableSearchComponent } from '@components/_base/base-search/base-sortable-search.component';
import { SortingPagingData } from '@helpers/sorting-paging-data';
import { displayError } from '@app/_utils/error-util';
import { StatusCode } from '@app/_enums/status-code';

@Component({
  selector: 'app-list-cashbacks',
  templateUrl: './list-cashbacks.component.html',
  styleUrls: ['./list-cashbacks.component.css']
})
export class ListCashbacksComponent extends BaseSortableSearchComponent<ListCashback> implements OnInit, OnDestroy {
  // Units
  statuses:         Status[];
  services:         Service[];
  serviceTypes:     ServiceType[];

  // Booleans
  loadPdfOrExcel = false;

  // Form
  searchForm = this.formBuilder.group({
    serviceType:     [null],
    service:         [null],
    status:          [null]
  });

  // Filtered services based on service type
  filteredServices: Service[] = [];

  // Observables
  searchSubscription: Subscription;
  search$ = this.searchSubject.asObservable().pipe(
    tap(() => this.dataLoaded = false),
    concatMap(() => {
      return this.cashbackService.pageableCashbacks(this.sortingPaging, this.searchForm.value).pipe(
        tap(([page, totalCount]) => {
          this.sortingPaging.fromRow = page.fromRow;
          this.sortingPaging.toRow = page.toRow;
          this.sortingPaging.totalElements = totalCount;
          this.content = page.content;
          this.dataLoaded = true;
        })
      )
    }),
    catchError(err => {
      displayError(err);
      this.dataLoaded = false;
      this.errorMessageSubject.next(this.translateService.instant("messages.errorLoadingData"));
      return EMPTY;
    })
  );

  loadSearchForm$ = this.route.queryParams.pipe(
    concatMap(params => {
      return forkJoin([
        this.nomenclatureService.getStatusesByType(this.checkStatuses()),
        this.nomenclatureService.findAllValidServices(),
        this.nomenclatureService.getServiceTypes()
      ]).pipe(
        tap(([statuses, services, serviceTypes]) => {
          this.statuses = statuses;
          this.services = services;
          this.serviceTypes = serviceTypes;
          this.filteredServices = services; // Initialize with all services
          this.searchSubject.next();
        }),
        catchError(err => {
          displayError(err);
          this.errorMessageSubject.next(this.translateService.instant("messages.errorLoadingData"));
          return EMPTY;
        }),
        repeatWhen(() => this.reload$)
      );
    })
  );

  constructor(
    private formBuilder: FormBuilder,
    public perms: PermissionsService,
    private cashbackService: CashbackService,
    private nomenclatureService: NomenclatureService,
    private translateService: TranslateService,
    private route: ActivatedRoute
  ) {
    super();
  }

  ngOnInit() {
    this.searchSubscription = this.search$.subscribe();
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  checkStatuses() {
    return StatusCode.PromotionDiscount;
  }

  get canAddEdit() {
    return this.perms.hasAccess(this.perms.CAN_VIEW_MARKETING_MENU);
  }

  get canView() {
    return this.perms.hasAccess(this.perms.CAN_VIEW_MARKETING_MENU);
  }

  onServiceTypeChange(event: any) {
    // Get the selected service type from the form control
    const selectedServiceType = this.searchForm.get('serviceType')?.value;
    
    if (selectedServiceType && selectedServiceType.id) {
      // Filter services by service type
      const filtered = this.services.filter(service => {
        const matches = service.serviceType && service.serviceType.id === selectedServiceType.id;
        return matches;
      });
      
      // Create a new array to trigger change detection
      this.filteredServices = [...filtered];
    } else {
      // Show all services
      this.filteredServices = [...this.services];
    }
    
    // Clear the service selection when service type changes
    this.searchForm.get('service')?.setValue(null);
  }
}
